import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AnnouncementsService, Announcement, Template, ContentLinkTypeList, futureDateValidator, ContentLinkType } from '../../../../lib/announcements.service';
import { ModalService } from '../../../../lib/modal.service';
import { LogService } from '../../../../lib/log.service';
import { Organization } from 'app/models/organization';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
@Component({
    selector: 'app-announcement-edit',
    templateUrl: './announcement-edit.component.html',
    styleUrls: ['./announcement-edit.component.scss'],
    standalone: false
})
export class AnnouncementEditComponent implements OnInit {
  announcementForm: FormGroup;
  templates: Template[] = [];
  isEditing = false;
  isNew = false;
  submitButtonPressed = false;
  config: any;
  organizations: Organization[] = [];
  linkOptions: string[] = ContentLinkTypeList;
  public Editor = ClassicEditor;
  constructor(
    private formBuilder: FormBuilder,
    private announcementsService: AnnouncementsService,
    private modalService: ModalService,
    private logService: LogService,
    public dialogRef: MatDialogRef<AnnouncementEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.announcementForm = this.formBuilder.group({
      OrgID: [null, Validators.required],
      TemplateID: [null],
      Title: ['', Validators.required],
      Content: ['', Validators.required],
      Link: [''],
      StartDate: [null, Validators.required],
      EndDate: [null, Validators.required]
    });
    this.config = {
      toolbar: [
          'bold',
          'italic',
          'strikethrough',
          '|',
          'numberedList',
          'bulletedList',
          '|',
          'link',
          'pasteFromOffice'
        ]
    };
    this.data = data.data;
    this.organizations = this.data.organizations;
  }

  ngOnInit(): void {
    
    if (this.data?.announcementId) {
      if (this.data.isEditing) {
        this.isEditing = true;
      } else {
        this.isEditing = false;
      }
      this.loadAnnouncement(this.data.announcementId);
    }
    else {
      this.isNew = true;
    }
  }

  isViewMode(): boolean {
    return !this.isEditing && !this.isNew;
  }

  isStartDatePast(): boolean {
    const startDate = this.announcementForm.get('StartDate')?.value;
    if (!startDate) {
      return false;
    }

    const beginningOfToday = new Date();
    beginningOfToday.setHours(0, 0, 0, 0);

    return startDate < beginningOfToday;
  }

  minStartDate(): Date {
    if (this.isStartDatePast()) {
      return;
    }
    return new Date();
  }

  minEndDate(): Date {
      const startDate = this.announcementForm.get('StartDate')?.value;
      if (!startDate) return null;
      const minEndDate = new Date(startDate);
      minEndDate.setDate(minEndDate.getDate() + 1);
      return minEndDate;
  }

  startDateInvalid(): boolean {
    return !this.announcementForm.get('StartDate')?.disabled && this.announcementForm.get('StartDate')?.invalid && (this.announcementForm.get('StartDate')?.touched || this.submitButtonPressed);
  }

  loadTemplates(): void {
    this.announcementsService.getTemplates().subscribe(
      (templates) => {
        if (this.announcementForm.get('OrgID')?.value) {
          const org = this.organizations.find(o => o.id === this.announcementForm.get('OrgID')?.value);
          const serviceType = org.serviceType || org?.parentOrganization?.serviceType;
          this.templates = templates.filter(t => t.OrgType ===serviceType);
          if (this.templates.length === 0) {
            this.templates = templates;
          }
        }
        else {
          this.templates = templates;
        }
        // Add "None" option at the beginning of the templates array
      },
      (error) => {
        this.logService.error(`Error loading templates: ${error}`);
      }
    );
  }

  loadAnnouncement(id: number): void {
    this.announcementsService.getAnnouncement(id).subscribe(
      (announcement) => {
        this.announcementForm.patchValue({
          OrgID: announcement.OrgID,
          TemplateID: announcement.TemplateID,
          Title: announcement.Title,
          Content: announcement.Content,
          Link: announcement.Link,
          StartDate: announcement.StartDate ? new Date(announcement.StartDate) : null,
          EndDate: announcement.EndDate ? new Date(announcement.EndDate) : null,
        });
        this.loadTemplates();
      },
      (error) => {
        this.logService.error(`Error loading announcement: ${error}`);
        this.modalService.showAlert('Error', 'Failed to load announcement');
        this.dialogRef.close();
      }
    );
  }

  onTemplateSelect(templateId: number | null): void {
    if (templateId === null) {
      // None option selected - clear the form fields
      this.announcementForm.patchValue({
        Title: '',
        Content: '',
        Link: '',
      });
      return;
    }
    
    const template = this.templates.find(t => t.ID === templateId);
    if (template) {
      this.announcementForm.patchValue({
        Title: template.Title,
        Content: template.Content,
        Link: template.Link,
      });
    }
  }

  onOrganizationSelect(organizationId: number): void {
    this.loadTemplates();
  }

  onLinkSelect(link: string): void {
    const content = this.announcementForm.get('Content')?.value;
    if (link === ContentLinkType.NONE) {
        if (content.includes('Click <a href="">here</a> to begin!')) {
            this.announcementForm.patchValue({
                Content: content.replace('Click <a href="">here</a> to begin!', '')
            });
        }
    } else {
        if (!content.includes('Click <a href="">here</a> to begin!')) {
            this.announcementForm.patchValue({
                Content: content + 'Click <a href="">here</a> to begin!'
            });
        }
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    this.submitButtonPressed = true;
    if (this.isViewMode()) {
        this.dialogRef.close();
        return;
      }
    if (this.announcementForm.valid) {
      const announcementData = this.announcementForm.value;
      
      if (this.isEditing && this.data?.announcementId) {
        this.announcementsService.updateAnnouncement(this.data.announcementId, announcementData).subscribe(
          () => {
            this.dialogRef.close(true);
            this.modalService.showAlert('Success', 'Announcement updated successfully');
          },
          (error) => {
            this.logService.error(`Error updating announcement: ${error}`);
            this.modalService.showAlert('Error', 'Failed to update announcement' + JSON.stringify(error));
          }
        );
      } else {
        this.announcementsService.createAnnouncement(announcementData).subscribe(
          () => {
            this.dialogRef.close(true);
            this.modalService.showAlert('Success', 'Announcement created successfully');
          },
          (error) => {
            this.logService.error(`Error creating announcement: ${error}`);
            this.modalService.showAlert('Error', 'Failed to create announcement' + JSON.stringify(error));
          }
        );
      }
    }
  }
} 